import React from 'react';
import { Box, Typography, CardContent, keyframes } from '@mui/material';
import PrimaryButton from './PrimaryButton';
import SecondaryButton from './SecondaryButton';
import CustomCard from './CustomCard';
import RenterOnboardingForm from './RenterOnboardingForm';
import union_logo from '../assets/Union.svg';

interface TourSlot {
  id: string;
  date: string;
  start_time_local: string;
  end_time_local: string;
  available_spots: number;
  status?: string;
}

const waveAnimation = keyframes`
  0% { transform: rotate(0deg); }
  25% { transform: rotate(5deg); }
  50% { transform: rotate(0deg); }
  75% { transform: rotate(-5deg); }
  100% { transform: rotate(0deg); }
`;

const slideUpFadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

interface ProspectTourSuccessProps {
  isMobile: boolean;
  propertyAddress: string;
  selectedTimeSlot: (TourSlot & { date: string }) | null;
  formatDate: (d: string) => string;
  formatTime: (t: string) => string;
  showAdCard: boolean;
  showOnboardingForm: boolean;
  onGetStarted: () => void;
  onboardingFormRef: React.RefObject<HTMLDivElement | null>;
}

const ProspectTourSuccess: React.FC<ProspectTourSuccessProps> = ({
  isMobile,
  propertyAddress,
  selectedTimeSlot,
  formatDate,
  formatTime,
  showAdCard,
  showOnboardingForm,
  onGetStarted,
  onboardingFormRef,
}) => (
  <Box
    sx={{
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: isMobile ? '1rem' : '2rem',
    }}
  >
    {/* Tour Confirmation Card */}
    <CustomCard
      sx={{
        maxWidth: 600,
        width: '97%',
        p: 1,
        textAlign: 'left',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
          <Box
            component="img"
            src={union_logo}
            alt="Union Logo"
            sx={{ width: 100, height: 100, animation: `${waveAnimation} 2s ease-in-out infinite` }}
          />
        </Box>
        <Typography variant="h5" sx={{ mb: 4, mt: 1, fontWeight: 600, textAlign: 'center' }}>
          Tour confirmed! 🎉
        </Typography>
        <Box sx={{ bgcolor: '#f5f5f5', borderRadius: '8px', p: '16px 20px', mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>Tour Location:</Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.5 }}>
            📍 {propertyAddress.replace(', USA', '')}
          </Typography>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>Tour Time:</Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
            📅 {formatDate(selectedTimeSlot?.date || '')} at {formatTime(selectedTimeSlot?.start_time_local || '')}
          </Typography>
        </Box>
      </CardContent>
    </CustomCard>

    {/* Ad Card — fades in after 2.5s */}
    {showAdCard && (
      <Box
        sx={{
          maxWidth: 600,
          width: '97%',
          mt: 3,
          opacity: 0,
          animation: `${slideUpFadeIn} 1.4s ease-out forwards`,
        }}
      >
        <Box sx={{ bgcolor: '#111', borderRadius: '16px', p: { xs: 3, sm: 4 }, textAlign: 'left' }}>
          <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700, color: 'white' }}>
            ✨ Want more tours like this?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'grey.400', lineHeight: 1.5 }}>
            Flip the script. Let landlords come to you.
          </Typography>
          <Box sx={{ mb: 3, pl: 0.5 }}>
            {['📅 Set your tour availability once', '🔍 Tell us what you\'re looking for', '📩 Get tour invites from landlords', '🤝 Choose who to meet'].map((item, i) => (
              <Typography key={i} variant="body1" sx={{ mb: i < 3 ? 1.5 : 0, color: 'grey.300', lineHeight: 1.5, fontSize: '0.95rem' }}>
                {item}
              </Typography>
            ))}
          </Box>
          <Typography variant="body2" sx={{ mb: 3, color: 'grey.500', fontStyle: 'italic' }}>
            Finding a home shouldn't feel like a second job.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <PrimaryButton
              sx={{
                minWidth: 180,
                bgcolor: 'white',
                color: 'black',
                fontWeight: 600,
                transition: 'box-shadow 0.2s ease',
                '&:hover': { bgcolor: 'grey.100', boxShadow: '0 6px 16px rgba(255,255,255,0.15)' },
              }}
              onClick={onGetStarted}
            >
              Get Started
            </PrimaryButton>
            <SecondaryButton
              sx={{
                borderColor: 'grey.600',
                color: 'white',
                '&:hover': { borderColor: 'white', backgroundColor: 'transparent', color: 'white' },
              }}
              onClick={() => window.location.href = '/renters'}
            >
              Learn More
            </SecondaryButton>
          </Box>
        </Box>
      </Box>
    )}

    {/* Renter Onboarding Form */}
    {showOnboardingForm && (
      <Box ref={onboardingFormRef} sx={{ maxWidth: 600, width: '97%', mt: 3 }}>
        <RenterOnboardingForm isVisible={showOnboardingForm} />
      </Box>
    )}
  </Box>
);

export default ProspectTourSuccess;
